import { CoreContainer, LayoutConst } from "../../../core";
import ProjectsButchControllerUltimate from "../../../core/controllers/ProjectsButchControllerUltimate";
import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ProjectsGridActions, ProjectsGridSelector } from "../../reducers/ProjectsGridSlice";
import { useDispatch, useSelector } from "react-redux";
import { Avatar, FlexBox, useBreakpoint, useToasts } from "@brg/ui";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../../core/hooks/auth/useAuth";
import { AppTester } from "supporting";
import { useDesignerById } from "../../hooks/api/useDesignerById";
import { useTheme } from "styled-components";
import { computeDesigner } from "../../helpers/computeData";
import ExploreHseContextsExternal from "../exploreHseContextsNew/exploreHseContextsExternal";
import { submitForm } from "./utils";
import {
  ContentContainer,
  DescriptionBlock,
  DescriptionComment,
  DescriptionListItem,
  DescriptionTitle,
  LinkWithForm,
  Title,
} from "./styles";
import { DesignerTab } from "shared/constants/designerTab";
import { applyCountToString } from "../designer/components/designerSlider";
import { API, Endpoints, Project } from "@brg/api";
import { SlideNavigation } from "shared/components/slideNavigation";
import { useGetDesignerHandlers } from "../designer/support/handlers";
import { ProjectSearchBarSorting } from "shared/components/projectsSearchBar/components/projectSearchBarSorting";
import { SortingTypeIndex } from "shared/constants/sortingType";
import { EncodedStateFilter, useEncodedStateFilter } from "shared/components/Filters";
import { MobileFilterSort } from "../explore/exploreView";
import { SortIcon } from "shared/icons";
import { DropdownTabs } from "../exploreHseContextsNew/components/DropdownTabs";
import { FeedCloud } from "./components/FeedCloud";
import useQueryState from "shared/hooks/useQueryState";
import { ModerationType } from "../teams/const/ModerationTypes";
import { useProfile } from "@brg/auth";

const getUrl = (encodedState: string, ctx?: string, contextType?: string | undefined, isHse?: boolean) => {
  return `/${encodedState}`
}

const deduplicate = (array: any[]) => {
  return Array.from(new Set(array))
}

const FeedPage: FC = () => {
  return <EncodedStateFilter getUrl={getUrl}>
    <UserFeedPage />
  </EncodedStateFilter>
};

const UserFeedPage = () => {
  const {
    sorting,
    setSorting,
  } = useEncodedStateFilter();
  const auth = useAuth();
  const toasts = useToasts();
  const theme = useTheme();
  const dispatch = useDispatch();
  const tagIdRef = useRef<string | null>(null)
  const updatedCountRef = useRef<boolean | null>(null);
  const profile = useProfile();
  const isIqMediia = AppTester.isIqMediiia();
  const isMobile = useBreakpoint(LayoutConst.screen.m);
  const [activeTabKey, setActiveTabKey] = useState<DesignerTab | string>(DesignerTab.subscriptionsProjects);
  const [{ tagId }] = useQueryState();
  const projects = useSelector(ProjectsGridSelector)?.data?.['main']?.subscriptionsProjects?.data
  const [tabsCount, setTabsCount] = useState({});
  const [time, setTime] = useState(Date.now());
  const updateTabs = useCallback(() => {
    setTime(Date.now())
    updatedCountRef.current = false
  }, [setTime])

  const {
    t,
    i18n: { language },
  } = useTranslation();

  const allSubscribersTags = useMemo(() => {
    return [];
  } , [projects])

  const [designer] = useDesignerById(
    profile?.designerId || "",
    language,
    profile?.designerId,
  );

  const isFeed = useMemo(
    () => (activeTabKey === DesignerTab.subscriptionsProjects) 
  , [activeTabKey])

  const computedDesigner = useMemo(() => computeDesigner(designer), [designer]);

  const handlers = useGetDesignerHandlers(updateTabs, false, toasts, t)

  const onSortingChange = useCallback((s: SortingTypeIndex): void => {
    setSorting(s)
    setTime(Date.now())
  }, [setSorting]);

  const onMobileSortingClick = useCallback(() => {
    setSorting(Number(sorting) === 1 ? 2 : Number(sorting) === 2 ? 20 : Number(sorting) === 20 ? 30 : 1);
    setTime(Date.now())
  }, [sorting, setSorting]);

  const tabs = useMemo(() =>([
    {
      name: DesignerTab.subscriptionsProjects,
      entityType: DesignerTab.subscriptionsProjects,
      path: DesignerTab.subscriptionsProjects,
      filter: {
        accountId: profile?.accountId, 
        ...(tagId ? { tagIds: [tagId]} : {})
      },
    },
    {
      name: DesignerTab.subscribers,
      entityType: DesignerTab.subscribers,
      path: DesignerTab.subscribers,
      filter: {
        accountId: profile?.accountId 
      },
      isOverride: true,
    },
    {
      name: DesignerTab.subscriptions,
      entityType: DesignerTab.subscriptions,
      path: DesignerTab.subscriptions,
      filter: {
        accountId: profile?.accountId,
      },
      isOverride: true,
    },
    {
      name: DesignerTab.educationSubscribes,
      entityType: 'educationSubscribes',
      path: DesignerTab.educationSubscribes,
      filter: {
        accountId: profile?.accountId,
        moderationTypes: [ 
          ModerationType.ChannelGroupOrCourse, ModerationType.Channel
        ],
          statuses: [0, 3], 
          ownerOrMemberIds: [profile?.accountId]  
      },
      isOverride: true,
    },
  ]),[profile, time, tagId])

  const sortingMethods = useMemo(() => {
    return [1, 2, 20, 30];
  }, [])


  const sortBar = useMemo(
    () => {
      if (!isFeed) return null
      if (isMobile) {
        return (
          <MobileFilterSort onClick={onMobileSortingClick}>
          <SortIcon />
          {t(`sorting_type.${Number(sorting) === 1 ? 'latest' : Number(sorting) === 2 ? 'popular' : Number(sorting) === 20 ? 'relevant' : Number(sorting) === 30 ? 'shop' : 'latest'}`)}
        </MobileFilterSort>
        )
      }

      return (
        <ProjectSearchBarSorting
        selectedSorting={sorting ?? 1}
        onSortingChange={onSortingChange}
        sortingMethods={sortingMethods}
      />
      )
    },
    [
      sorting,
      isFeed,
      isMobile,
      sortingMethods
    ],
  );

  const projectsButchFilter = useMemo(() => {
    return {
      limit: 40,
      sort: Number(sorting) === 30 ? 15 : sorting,
      ...(!!tagId ? { tagIds: [tagId]} : {}),
      ...(Number(sorting) === 30 ? { hasProductCards: true } : {})
    }
  } , [sorting, tagId, time])

  useEffect(() => {
    dispatch(
      ProjectsGridActions.setTabs({
        tabs,
        categories: [],
        mainFilter: {},
        context: "feed",
      })
    );
  }, [time, profile]);

  useEffect(() => {
    if (tabs && tabsCount && !updatedCountRef.current) {
      API
        .request()
        .endpoint(Endpoints.geograffee)
        .url('/api/Designer/GetTabsItemsCount')
        .body({
          tabs,
        })
        .post()
        .release()
        .then((r) => {
          const count = r?.tabs?.reduce((acc: any, { name, count }: any) => {
            return {
              ...acc,
              [name]: count,
            }
          }, {
          })

          setTabsCount(count);
          updatedCountRef.current = true;
        });
    }
  }, [tabs, time, profile, updatedCountRef])

  useEffect(() => {
    if (tagIdRef && tagId && tagIdRef.current !== tagId) {
      updateTabs()
      updatedCountRef.current = false;
    }
    tagIdRef.current = tagId
  } , [tagIdRef, tagId])

  const tabsFormatted = useMemo(() => {
    if (!tabsCount || !Object.keys(tabsCount)?.length) {
      return {}
    }
    //@ts-ignore
    return tabs.reduce((acc: any[], tab: any) => {
      const {
        //@ts-ignore
        entityType,
        name,
        filter: {
          //@ts-ignore
          bookStatus,
          //@ts-ignore
          projectStatus,
          ...filter
        }
      } = tab;
      //@ts-ignore
      if (tabsCount[name] > 0) {
        return {...acc, [name]:  applyCountToString(tabsCount, DesignerTab?.[name as keyof {}], t(`${name}`)).toLowerCase()}
      }
      return acc;
    }, {})
  }, [tabs, tabsCount, time, language])

  const widthAndHeight = isMobile ? "166px" : "270px";

  const onTabChange = (tab: DesignerTab | string) => {
    setActiveTabKey(tab);
  };

  const navigation = useMemo(() => {
    if (isMobile) {
      return (
        <FlexBox alignMobile='center' align={'center'} justify='flex-start' justifyMobile='flex-start' widthMobile={'100%'} width={'100%'}>
          <DropdownTabs
            whiteTheme={false}
            activeTabKey={activeTabKey}
            mobileShrink={true}
            items={tabsFormatted}
            onChange={(tab) => {
              setActiveTabKey(tab as string);
              sessionStorage.setItem('lastTab', tab)
            }}
          />
        </FlexBox> 
      )
    }

    return (
      <SlideNavigation 
        activeIndexKey={activeTabKey}
        onChange={onTabChange}
        items={tabsFormatted}
        isLoading={false}
      />
    )
  } , [
    activeTabKey,
    isMobile,
    tabsFormatted
  ])

  const tagsCloud = useMemo(() => {
    if (activeTabKey === DesignerTab.subscriptionsProjects && profile) {
      return (
        <FlexBox offsetBottom={24} offsetBottomMobile={16}>
          <FeedCloud accountId={profile?.designerId} onUpdate={updateTabs} />
        </FlexBox>
      )
    }

    return null;
  } , [activeTabKey, allSubscribersTags, profile])

  const subscribeLink = useMemo(
    () => (
      <LinkWithForm
        onClick={() =>
          submitForm({
            email: designer?.email || "_",
            token: auth.token || "_",
            name: designer ? `${designer?.name || ''} ${designer?.surName || ''}` : "_",
          })
        }
      >
        Подписаться
      </LinkWithForm>
    ),
    [auth.token, designer]
  );

  if (!profile) {
    return null
  }

  const linkToTags = <LinkWithForm href="/lk/tags">Подписаться</LinkWithForm>

  return (
    <CoreContainer isIqMedia={isIqMediia}>
      {tagsCloud}
      {isIqMediia ? (
        <FlexBox
          style={{
            marginBottom: isMobile ? "0" : "32px",
            gap: "24px",
          }}
          direction={isMobile ? "column" : "row"}
        >
          <div
            style={{
              minWidth: widthAndHeight,
              width: widthAndHeight,
              height: widthAndHeight,
              position: "relative",
            }}
          >
            <Avatar
              imgBackColor={theme.app.backgroundColor}
              {...computedDesigner}
              fs={90}
              shadow
            />
          </div>
          <FlexBox
            direction={"column"}
            width={"100%"}
            style={{ minHeight: "100px", position: "relative", marginLeft: 0 }}
            justify="space-between"
          >
            <FlexBox
              style={{
                minHeight: "auto",
                position: "relative",
                marginBottom: "10px",
              }}
              align={"flex-start"}
              justify="space-between"
            >
              <FlexBox>
                <Title color={theme.designerPage.textColor}>
                  {computedDesigner.fullName}
                </Title>
              </FlexBox>
            </FlexBox>

            <DescriptionBlock>
              <DescriptionTitle>
                Это ваш личный кабинет, здесь вы можете:{" "}
              </DescriptionTitle>

              <DescriptionListItem>▪️ {subscribeLink} на рассылку IQ Media</DescriptionListItem>
              <DescriptionListItem>▪️ {linkToTags} на интересующих вас авторов и темы</DescriptionListItem>
              <DescriptionListItem>▪️ Сформировать личную ленту</DescriptionListItem>
              <DescriptionListItem>▪️ Оценивать и комментировать материалы</DescriptionListItem>

              <DescriptionComment>
                Функционал личного кабинета читателя находится в доработке, в
                будущем здесь будет ещё больше полезных функций - покупка
                курсов, получение доступа к закрытым материалам, ведение
                собственного блога и многое другое.
              </DescriptionComment>
            </DescriptionBlock>
          </FlexBox>
        </FlexBox>
      ) : (
        <FlexBox direction="row" directionMobile="row" justify="space-between">

          {navigation}
          {sortBar}
        </FlexBox>
      )}
      {isIqMediia ? (
        <ExploreHseContextsExternal 
          context="iqmedia" 
        />
      ) : (
        <ContentContainer>
          <ProjectsButchControllerUltimate
            filter={projectsButchFilter}
            accountId={profile?.designerId}
            currentCategory="main"
            currentTab={activeTabKey}
            // withoutRepost
            contextName="feed"
            isMainPage
            handlers={handlers}
            isMobileMoreVisible
            emptyMsgText={t('empty_channel_text')}
          />
        </ContentContainer>
      )}
    </CoreContainer>
  );
};

export default FeedPage;
import { CoreContainer, LayoutConst } from "../../../core";
import ProjectsButchControllerUltimate from "../../../core/controllers/ProjectsButchControllerUltimate";
import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ProjectsGridActions, ProjectsGridSelector } from "../../reducers/ProjectsGridSlice";
import { useDispatch, useSelector } from "react-redux";
import { Avatar, FlexBox, useBreakpoint, useToasts } from "@brg/ui";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../../core/hooks/auth/useAuth";
import { AppTester } from "supporting";
import { useDesignerById } from "../../hooks/api/useDesignerById";
import { useTheme } from "styled-components";
import { computeDesigner } from "../../helpers/computeData";
import ExploreHseContextsExternal from "../exploreHseContextsNew/exploreHseContextsExternal";
import { submitForm } from "./utils";
import {
  ContentContainer,
  DescriptionBlock,
  DescriptionComment,
  DescriptionListItem,
  DescriptionTitle,
  LinkWithForm,
  Title,
} from "./styles";
import { DesignerTab } from "shared/constants/designerTab";
import { applyCountToString } from "../designer/components/designerSlider";
import { API, Endpoints, Project } from "@brg/api";
import { SlideNavigation } from "shared/components/slideNavigation";
import { useGetDesignerHandlers } from "../designer/support/handlers";
import { ProjectSearchBarSorting } from "shared/components/projectsSearchBar/components/projectSearchBarSorting";
import { SortingTypeIndex } from "shared/constants/sortingType";
import { EncodedStateFilter, useEncodedStateFilter } from "shared/components/Filters";
import { MobileFilterSort } from "../explore/exploreView";
import { SortIcon } from "shared/icons";
import { DropdownTabs } from "../exploreHseContextsNew/components/DropdownTabs";
import { FeedCloud } from "./components/FeedCloud";
import useQueryState from "shared/hooks/useQueryState";
import { ModerationType } from "../teams/const/ModerationTypes";
import { useProfile } from "@brg/auth";

const getUrl = (encodedState: string, ctx?: string, contextType?: string | undefined, isHse?: boolean) => {
  return `/${encodedState}`
}

const deduplicate = (array: any[]) => {
  return Array.from(new Set(array))
}

const FeedPage: FC = () => {
  return <EncodedStateFilter getUrl={getUrl}>
    <UserFeedPage />
  </EncodedStateFilter>
};

const UserFeedPage = () => {
  const {
    sorting,
    setSorting,
  } = useEncodedStateFilter();
  const auth = useAuth();
  const toasts = useToasts();
  const theme = useTheme();
  const dispatch = useDispatch();
  const tagIdRef = useRef<string | null>(null)
  const updatedCountRef = useRef<boolean | null>(null);
  const profile = useProfile();
  const isIqMediia = AppTester.isIqMediiia();
  const isMobile = useBreakpoint(LayoutConst.screen.m);
  const [activeTabKey, setActiveTabKey] = useState<DesignerTab | string>(DesignerTab.subscriptionsProjects);
  const [{ tagId }] = useQueryState();
  const projects = useSelector(ProjectsGridSelector)?.data?.['main']?.subscriptionsProjects?.data
  const [tabsCount, setTabsCount] = useState({});
  const [time, setTime] = useState(Date.now());
  const updateTabs = useCallback(() => {
    setTime(Date.now())
    updatedCountRef.current = false
  }, [setTime])

  const {
    t,
    i18n: { language },
  } = useTranslation();

  const allSubscribersTags = useMemo(() => {
    return [];
  } , [projects])

  const [designer] = useDesignerById(
    profile?.designerId || "",
    language,
    profile?.designerId,
  );

  const isFeed = useMemo(
    () => (activeTabKey === DesignerTab.subscriptionsProjects) 
  , [activeTabKey])

  const computedDesigner = useMemo(() => computeDesigner(designer), [designer]);

  const handlers = useGetDesignerHandlers(updateTabs, false, toasts, t)

  const onSortingChange = useCallback((s: SortingTypeIndex): void => {
    setSorting(s)
    setTime(Date.now())
  }, [setSorting]);

  const onMobileSortingClick = useCallback(() => {
    setSorting(Number(sorting) === 1 ? 2 : Number(sorting) === 2 ? 20 : Number(sorting) === 20 ? 30 : 1);
    setTime(Date.now())
  }, [sorting, setSorting]);

  const tabs = useMemo(() =>([
    {
      name: DesignerTab.subscriptionsProjects,
      entityType: DesignerTab.subscriptionsProjects,
      path: DesignerTab.subscriptionsProjects,
      filter: {
        accountId: profile?.accountId, 
        ...(tagId ? { tagIds: [tagId]} : {})
      },
    },
    {
      name: DesignerTab.subscribers,
      entityType: DesignerTab.subscribers,
      path: DesignerTab.subscribers,
      filter: {
        accountId: profile?.accountId 
      },
      isOverride: true,
    },
    {
      name: DesignerTab.subscriptions,
      entityType: DesignerTab.subscriptions,
      path: DesignerTab.subscriptions,
      filter: {
        accountId: profile?.accountId,
      },
      isOverride: true,
    },
    {
      name: DesignerTab.educationSubscribes,
      entityType: 'educationSubscribes',
      path: DesignerTab.educationSubscribes,
      filter: {
        accountId: profile?.accountId,
        moderationTypes: [ 
          ModerationType.ChannelGroupOrCourse, ModerationType.Channel
        ],
          statuses: [0, 3], 
          ownerOrMemberIds: [profile?.accountId]  
      },
      isOverride: true,
    },
  ]),[profile, time, tagId])

  const sortingMethods = useMemo(() => {
    return [1, 2, 20, 30];
  }, [])


  const sortBar = useMemo(
    () => {
      if (!isFeed) return null
      if (isMobile) {
        return (
          <MobileFilterSort onClick={onMobileSortingClick}>
          <SortIcon />
          {t(`sorting_type.${Number(sorting) === 1 ? 'latest' : Number(sorting) === 2 ? 'popular' : Number(sorting) === 20 ? 'relevant' : Number(sorting) === 30 ? 'shop' : 'latest'}`)}
        </MobileFilterSort>
        )
      }

      return (
        <ProjectSearchBarSorting
        selectedSorting={sorting ?? 1}
        onSortingChange={onSortingChange}
        sortingMethods={sortingMethods}
      />
      )
    },
    [
      sorting,
      isFeed,
      isMobile,
      sortingMethods
    ],
  );

  const projectsButchFilter = useMemo(() => {
    return {
      limit: 40,
      sort: Number(sorting) === 30 ? 15 : sorting,
      ...(!!tagId ? { tagIds: [tagId]} : {}),
      ...(Number(sorting) === 30 ? { hasProductCards: true } : {})
    }
  } , [sorting, tagId, time])

  useEffect(() => {
    dispatch(
      ProjectsGridActions.setTabs({
        tabs,
        categories: [],
        mainFilter: {},
        context: "feed",
      })
    );
  }, [time, profile]);

  useEffect(() => {
    if (tabs && tabsCount && !updatedCountRef.current) {
      API
        .request()
        .endpoint(Endpoints.geograffee)
        .url('/api/Designer/GetTabsItemsCount')
        .body({
          tabs,
        })
        .post()
        .release()
        .then((r) => {
          const count = r?.tabs?.reduce((acc: any, { name, count }: any) => {
            return {
              ...acc,
              [name]: count,
            }
          }, {
          })

          setTabsCount(count);
          updatedCountRef.current = true;
        });
    }
  }, [tabs, time, profile, updatedCountRef])

  useEffect(() => {
    if (tagIdRef && tagId && tagIdRef.current !== tagId) {
      updateTabs()
      updatedCountRef.current = false;
    }
    tagIdRef.current = tagId
  } , [tagIdRef, tagId])

  const tabsFormatted = useMemo(() => {
    if (!tabsCount || !Object.keys(tabsCount)?.length) {
      return {}
    }
    //@ts-ignore
    return tabs.reduce((acc: any[], tab: any) => {
      const {
        //@ts-ignore
        entityType,
        name,
        filter: {
          //@ts-ignore
          bookStatus,
          //@ts-ignore
          projectStatus,
          ...filter
        }
      } = tab;
      //@ts-ignore
      if (tabsCount[name] > 0) {
        return {...acc, [name]:  applyCountToString(tabsCount, DesignerTab?.[name as keyof {}], t(`${name}`)).toLowerCase()}
      }
      return acc;
    }, {})
  }, [tabs, tabsCount, time, language])

  const widthAndHeight = isMobile ? "166px" : "270px";

  const onTabChange = (tab: DesignerTab | string) => {
    setActiveTabKey(tab);
  };

  const navigation = useMemo(() => {
    if (isMobile) {
      return (
        <FlexBox alignMobile='center' align={'center'} justify='flex-start' justifyMobile='flex-start' widthMobile={'100%'} width={'100%'}>
          <DropdownTabs
            whiteTheme={false}
            activeTabKey={activeTabKey}
            mobileShrink={true}
            items={tabsFormatted}
            onChange={(tab) => {
              setActiveTabKey(tab as string);
              sessionStorage.setItem('lastTab', tab)
            }}
          />
        </FlexBox> 
      )
    }

    return (
      <SlideNavigation 
        activeIndexKey={activeTabKey}
        onChange={onTabChange}
        items={tabsFormatted}
        isLoading={false}
      />
    )
  } , [
    activeTabKey,
    isMobile,
    tabsFormatted
  ])

  const tagsCloud = useMemo(() => {
    if (activeTabKey === DesignerTab.subscriptionsProjects && profile) {
      return (
        <FlexBox offsetBottom={24} offsetBottomMobile={16}>
          <FeedCloud accountId={profile?.designerId} onUpdate={updateTabs} />
        </FlexBox>
      )
    }

    return null;
  } , [activeTabKey, allSubscribersTags, profile])

  const subscribeLink = useMemo(
    () => (
      <LinkWithForm
        onClick={() =>
          submitForm({
            email: designer?.email || "_",
            token: auth.token || "_",
            name: designer ? `${designer?.name || ''} ${designer?.surName || ''}` : "_",
          })
        }
      >
        Подписаться
      </LinkWithForm>
    ),
    [auth.token, designer]
  );

  if (!profile) {
    return null
  }

  const linkToTags = <LinkWithForm href="/lk/tags">Подписаться</LinkWithForm>

  return (
    <CoreContainer isIqMedia={isIqMediia}>
      {tagsCloud}
      {isIqMediia ? (
        <FlexBox
          style={{
            marginBottom: isMobile ? "0" : "32px",
            gap: "24px",
          }}
          direction={isMobile ? "column" : "row"}
        >
          <div
            style={{
              minWidth: widthAndHeight,
              width: widthAndHeight,
              height: widthAndHeight,
              position: "relative",
            }}
          >
            <Avatar
              imgBackColor={theme.app.backgroundColor}
              {...computedDesigner}
              fs={90}
              shadow
            />
          </div>
          <FlexBox
            direction={"column"}
            width={"100%"}
            style={{ minHeight: "100px", position: "relative", marginLeft: 0 }}
            justify="space-between"
          >
            <FlexBox
              style={{
                minHeight: "auto",
                position: "relative",
                marginBottom: "10px",
              }}
              align={"flex-start"}
              justify="space-between"
            >
              <FlexBox>
                <Title color={theme.designerPage.textColor}>
                  {computedDesigner.fullName}
                </Title>
              </FlexBox>
            </FlexBox>

            <DescriptionBlock>
              <DescriptionTitle>
                Это ваш личный кабинет, здесь вы можете:{" "}
              </DescriptionTitle>

              <DescriptionListItem>▪️ {subscribeLink} на рассылку IQ Media</DescriptionListItem>
              <DescriptionListItem>▪️ {linkToTags} на интересующих вас авторов и темы</DescriptionListItem>
              <DescriptionListItem>▪️ Сформировать личную ленту</DescriptionListItem>
              <DescriptionListItem>▪️ Оценивать и комментировать материалы</DescriptionListItem>

              <DescriptionComment>
                Функционал личного кабинета читателя находится в доработке, в
                будущем здесь будет ещё больше полезных функций - покупка
                курсов, получение доступа к закрытым материалам, ведение
                собственного блога и многое другое.
              </DescriptionComment>
            </DescriptionBlock>
          </FlexBox>
        </FlexBox>
      ) : (
        <FlexBox direction="row" directionMobile="row" justify="space-between">

          {navigation}
          {sortBar}
        </FlexBox>
      )}
      {isIqMediia ? (
        <ExploreHseContextsExternal 
          context="iqmedia" 
        />
      ) : (
        <ContentContainer>
          <ProjectsButchControllerUltimate
            filter={projectsButchFilter}
            accountId={profile?.designerId}
            currentCategory="main"
            currentTab={activeTabKey}
            // withoutRepost
            contextName="feed"
            isMainPage
            handlers={handlers}
            isMobileMoreVisible
            emptyMsgText={t('empty_channel_text')}
          />
        </ContentContainer>
      )}
    </CoreContainer>
  );
};

export default FeedPage;
